import {
  Column,
  Columns,
  Icon,
  IconButton,
  Text,
} from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { CSSProperties, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Outlet, useNavigate } from 'react-router-dom';

import { useColorMode } from 'app/utils/colorMode';
import { useServerStorage } from 'utils/useServerStorage';

import styles from './tvDashboard.view.module.scss';

export type TvDashboardViewOutletContext = {
  setTitle: (title: string) => void;
};

export const TvDashboardView = (): JSX.Element => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');

  const storage = useServerStorage('user');
  const { isColorModeSupported } = useColorMode();

  // We check specifically for the presence of a `colorMode` in serverStorage rather than the value
  // from `useColorMode` to determine whether the user has set a color mode preference. If not, the
  // TV dashboard is rendered in dark mode by default. Otherwise we respect the user preference.
  const hasColorMode = isColorModeSupported && !!storage.data.global?.colorMode;

  return (
    <Columns
      style={
        {
          '--tv-dashboard-background': hasColorMode
            ? colors.neutral['000']
            : colors.neutral.values['000'].dark,
          '--tv-dashboard-text-color': hasColorMode
            ? typography.color.primary
            : typography.color.values['primary'].dark,
          '--tv-dashboard-border-color': hasColorMode
            ? colors.neutral['200']
            : colors.neutral.values['200'].dark,
        } as CSSProperties
      }
    >
      <Column className={styles.root}>
        <Column.Row slottable className={styles.header}>
          <Column.Slot placement="start">
            <IconButton
              icon="arrow-left"
              variant="tertiary"
              onClick={() => navigate(-1)}
            />
          </Column.Slot>

          <Column.Slot placement="center">
            <Icon name="dixa" size={24} color="secondary" />
          </Column.Slot>

          <Column.Slot placement="end">
            {title && (
              <Text.Heading variant="heading-2" color="tertiary">
                {title}
              </Text.Heading>
            )}
          </Column.Slot>
        </Column.Row>

        <Column.Row
          scrollable
          noVerticalPadding
          noHorizontalPadding
          className={styles.main}
        >
          <Outlet
            context={{ setTitle } satisfies TvDashboardViewOutletContext}
          />
        </Column.Row>
      </Column>
    </Columns>
  );
};
